import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Grow,
  Link as MUILink,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

function Hero({
  eyebrow,
  title,
  subtitle,
  bulletList,
  image,
  overlay = true,
  align = "left",
  maxWidth = "md",
  primaryAction,
  secondaryAction,
  sx,
}) {
  const theme = useTheme();

  const alignMap = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
  };

  const content = (
    <Box
      className="box-default"
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        p: { xs: 4, sm: 8 },
      }}
    >
      <Stack
        spacing={{ xs: 1, sm: 2 }}
        sx={{
          width: 1,
          textAlign: align,
          alignItems: { sm: alignMap[align] },
          pb: { xs: 1, sm: 2 },
        }}
      >
        {eyebrow && (
          <Typography
            variant="body2"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 3,
            }}
          >
            {eyebrow}
          </Typography>
        )}

        <Grow in timeout={1000}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
            }}
          >
            {title}
          </Typography>
        </Grow>

        {subtitle && (
          <Grow in timeout={1500}>
            <Typography
              variant="h6"
              className="text-paragraph"
              sx={{
                color: "white",
                opacity: 0.6,
              }}
            >
              {subtitle}
            </Typography>
          </Grow>
        )}

        {bulletList &&
          bulletList.map((item, index) => (
            <Box
              key={index}
              className="box-default"
              sx={{
                pt: index === 0 ? { xs: 2, sm: 4 } : 0,
              }}
            >
              {item.spacer ? (
                <Box sx={{ height: item.height }} />
              ) : (
                <Chip
                  label={item.text}
                  variant="outlined"
                  sx={{
                    borderColor: "rgba(255, 255, 255, 0.7)",
                    color: "common.white",
                    fontWeight: 500,
                    opacity: 0.8,
                    "& .MuiChip-icon": {
                      color: "common.white",
                      opacity: 0.8,
                    },
                    "&:hover": {
                      borderColor: "common.white",
                      opacity: 1,
                      "& .MuiChip-icon": {
                        color: "common.white",
                        opacity: 1,
                      },
                    },
                    cursor: item.onClick ? "pointer" : "default",
                  }}
                  icon={item.icon ? item.icon : null}
                  onClick={item.onClick ? item.onClick : null}
                />
              )}
            </Box>
          ))}

        {(primaryAction || secondaryAction) && (
          <Box
            className="box-inline"
            sx={{
              pt: { xs: 4, sm: 8 },
            }}
          >
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
              sx={{
                pt: 1,
                alignItems: { xs: "stretch", sm: alignMap[align] },
                "& .MuiButton-root": { minWidth: 160 },
              }}
            >
              {primaryAction && (
                <HeroButton action={primaryAction} color="primary" />
              )}
              {secondaryAction && (
                <HeroButton action={secondaryAction} color="secondary" />
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );

  return (
    <Box
      component="section"
      role="banner"
      sx={{
        position: "relative",
        display: "grid",
        placeItems: "stretch",
        color: image ? "common.white" : undefined,
        backgroundColor: image ? undefined : "background.default",
        backgroundImage: image ? `url(${image})` : undefined,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
        ...sx,
      }}
    >
      {image && overlay && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(0deg, ${alpha(
              theme.palette.common.black,
              theme.palette.mode === "dark" ? 0.6 : 0.5,
            )}, ${alpha(
              theme.palette.common.black,
              theme.palette.mode === "dark" ? 0.4 : 0.25,
            )})`,
          }}
        />
      )}

      {!image && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(1200px 400px at 10% 10%, ${alpha(
              theme.palette.primary.main,
              0.1,
            )}, transparent 50%), radial-gradient(1200px 400px at 90% 90%, ${alpha(
              theme.palette.secondary.main,
              0.08,
            )}, transparent 50%)`,
          }}
        />
      )}

      {content}
    </Box>
  );
}

function HeroButton({ action, color = "primary" }) {
  const { label, href, onClick, variant = "contained" } = action;
  const asLink = !!href;

  if (asLink) {
    return (
      <Button
        component={MUILink}
        href={href}
        variant={variant}
        color={color}
        onClick={onClick}
        underline="none"
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      color={color}
      onClick={onClick}
      sx={{
        borderRadius: 3,
      }}
    >
      {label}
    </Button>
  );
}

export default Hero;
